import { useTheme, useToastProvider } from "@deere/fuel-react";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { GridSortModel } from "@mui/x-data-grid-pro";
import { useOktaAuth } from "@okta/okta-react";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import OtherDealerInventoryGrid from "./components/OtherDealerInventoryGrid/OtherDealerInventoryGrid";
import { FilterState as ColumnFilterState } from "./components/OtherDealerInventoryGrid/types";
import OtherDealerInventoryHeader from "./components/OtherDealerInventoryHeader/OtherDealerInventoryHeader";
import {
  onFirstPageClick,
  onLastPageClick,
  onNextClick,
  onPreviousPageClick,
  onRowsPerPageChange,
} from "./paginationUtils";
import { ActionTypes, initialState, reducer } from "./reducer/reducer";
import d2dApi from "../../../api/d2d/d2dApi";
import partsApi from "../../../api/parts/partsApi";
import Pagination from "../../../components/DataGridPro/Pagination/Pagination";
import Link from "../../../components/Link/Link";
import StickyFooter from "../../../components/StickyFooter/StickyFooter";
import { useConfigContext } from "../../../context/ConfigContext";
import { DealerAccountContext } from "../../../context/DealerAccountContext";

// Shape of raw dealer item returned by the API (fields are optional / variant).
interface RawDealerItem {
  account?: string;
  accountId?: string;
  dealershipName?: string;
  accountName?: string;
  city?: string;
  state?: string;
  country?: string;
  phone?: string;
  inventoryBalance?: number;
  source?: string;
  comment?: string;
}

interface MappedDealerItem {
  id: string;
  account: string;
  dealershipName: string;
  city: string;
  state: string;
  country: string;
  phone: string;
  inventoryBalance: string;
  source: string;
  comment: string;
  partNumber: string;
}

// Convert raw API dealer list into the shape our grid expects.
// Keeps mapping logic isolated and readable.
const mapDealers = (
  dealers: RawDealerItem[],
  partNumber: string
): MappedDealerItem[] =>
  dealers.map((dealer: RawDealerItem, index: number) => ({
    id: String(index),
    account: String(dealer.account || dealer.accountId || ""),
    dealershipName: String(dealer.dealershipName || dealer.accountName || ""),
    city: String(dealer.city || ""),
    state: String(dealer.state || ""),
    country: String(dealer.country || ""),
    phone: String(dealer.phone || ""),
    inventoryBalance: String(dealer.inventoryBalance ?? 0),
    source: String(dealer.source ?? ""),
    comment: String(dealer.comment ?? ""),
    partNumber,
  }));

const OtherDealerInventory = () => {
  const { t, i18n } = useTranslation(["d2d", "common"]);
  const theme = useTheme();
  const { dealerAccount } = useContext(DealerAccountContext);
  const {
    D2D_OTHER_DEALER_INVENTORY_API_URL,
    PARTS_SERVICE_URL,
    DEALER_SERVICE_URL,
  } = useConfigContext();
  const { authState } = useOktaAuth();
  const { enqueueToast } = useToastProvider();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [searchParams] = useSearchParams();
  const initialPartNumber = searchParams.get("partNumber") ?? "";
  const hasAutoSearchedRef = useRef(false);

  // Standalone part number input (drives backend search)
  const [partNumberSearch, setPartNumberSearch] = useState(initialPartNumber);
  const [columnFilters, setColumnFilters] = useState<ColumnFilterState>({
    account: "",
    dealershipName: "",
    city: "",
    state: "",
  });
  const [rawData, setRawData] = useState<any[]>([]);
  // Header distance controls (currently informational; does not alter search logic)
  const [distanceLimit, setDistanceLimit] = useState<number>(0);
  const [distanceUnit, setDistanceUnit] = useState<string>("MI");
  const [hasSearched, setHasSearched] = useState(false);
  const [searchedPartNumber, setSearchedPartNumber] = useState<string>("");
  const [partName, setPartName] = useState<string>("");

  // Removed debounce-based auto fetch: search now only executed via explicit submit button
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Perform the manual GET search. Guard clauses keep code linear & readable.
  const performSearch = useCallback(async () => {
    // Clear data if auth/account missing.
    if (!authState?.isAuthenticated || !dealerAccount?.id) {
      setRawData([]);
      return;
    }
    const trimmed = partNumberSearch.trim();
    if (!trimmed) {
      setRawData([]);
      return;
    }
    const normalizedPartNumber = trimmed.toUpperCase();
    const token = authState.accessToken?.accessToken || "";

    dispatch({ type: ActionTypes.SET_LOADING, payload: true });
    dispatch({ type: ActionTypes.SET_ERROR, payload: false });
    try {
      const [d2dResp, partsResp] = await Promise.all([
        d2dApi.fetchOtherDealersInventory(
          D2D_OTHER_DEALER_INVENTORY_API_URL,
          token,
          normalizedPartNumber,
          dealerAccount.id
        ),
        partsApi
          .searchWithLocale(PARTS_SERVICE_URL, token, {
            partNumbers: [normalizedPartNumber],
            accountId: dealerAccount.id,
            language: i18n.language,
          })
          .catch(() => []),
      ]);

      const dealers: RawDealerItem[] = d2dResp?.dealers || [];
      setRawData(mapDealers(dealers, normalizedPartNumber));
      setSearchedPartNumber(normalizedPartNumber);
      setPartName(partsResp?.[0]?.description || "");
    } catch {
      dispatch({ type: ActionTypes.SET_ERROR, payload: true });
      enqueueToast({
        variant: "error",
        message: "Failed to fetch Other Dealer Inventory",
        isCloseable: true,
      });
      setRawData([]);
    } finally {
      dispatch({ type: ActionTypes.SET_LOADING, payload: false });
    }
  }, [
    authState?.isAuthenticated,
    authState?.accessToken?.accessToken,
    dealerAccount?.id,
    D2D_OTHER_DEALER_INVENTORY_API_URL,
    PARTS_SERVICE_URL,
    i18n.language,
    enqueueToast,
    partNumberSearch,
  ]);

  // Trigger search only when user presses Enter (explicit trigger added via toolbar submit) - no automatic fetch on part change.

  const { processedItems, totalFilteredItems } = useMemo(() => {
    let processed = [...rawData];

    if (columnFilters.account) {
      processed = processed.filter((i) =>
        i.account.toLowerCase().includes(columnFilters.account.toLowerCase())
      );
    }
    if (columnFilters.dealershipName) {
      processed = processed.filter((i) =>
        i.dealershipName
          .toLowerCase()
          .includes(columnFilters.dealershipName.toLowerCase())
      );
    }
    if (columnFilters.city) {
      processed = processed.filter((i) =>
        i.city.toLowerCase().includes(columnFilters.city.toLowerCase())
      );
    }
    if (columnFilters.state) {
      processed = processed.filter((i) =>
        i.state.toLowerCase().includes(columnFilters.state.toLowerCase())
      );
    }

    if (state.sortModel.length > 0) {
      const { field, sort } = state.sortModel[0];
      processed = [...processed].sort((a: any, b: any) => {
        const av = a[field];
        const bv = b[field];
        if (av === bv) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (av < bv) return sort === "asc" ? -1 : 1;
        return sort === "asc" ? 1 : -1;
      });
    }

    const totalItems = processed.length;

    const page = Math.max(1, state.currentPage);
    const pageSize = Math.max(1, state.pageSize);
    const start = (page - 1) * pageSize;
    const paginated = processed.slice(start, start + pageSize);

    return {
      processedItems: paginated,
      totalFilteredItems: totalItems,
    };
  }, [
    rawData,
    columnFilters,
    state.sortModel,
    state.currentPage,
    state.pageSize,
  ]);

  const handleSortModelChange = (model: GridSortModel) => {
    if (model.length > 0) {
      dispatch({
        type: ActionTypes.SET_SORT_MODEL,
        payload: [{ field: model[0].field, sort: model[0].sort || "asc" }],
      });
    } else {
      dispatch({
        type: ActionTypes.SET_SORT_MODEL,
        payload: initialState.sortModel,
      });
    }
  };

  const handlePartNumberSearchChange = (value: string) => {
    setPartNumberSearch(value);
    dispatch({ type: ActionTypes.SET_CURRENT_PAGE, payload: 1 });
    // No automatic backend call here; user must click Search
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (!value.trim()) {
      setRawData([]); // clear grid when input cleared
      setSearchedPartNumber("");
      setPartName("");
    }
  };

  // Removed auto-fetch effect to avoid repeated calls on failing API.

  const handleColumnFilterChange = (
    field: keyof ColumnFilterState,
    value: string
  ) => {
    setColumnFilters((prev) => ({ ...prev, [field]: value }));
    dispatch({ type: ActionTypes.SET_CURRENT_PAGE, payload: 1 });
  };

  const handleClearColumnFilter = (field: keyof ColumnFilterState) => {
    setColumnFilters((prev) => ({ ...prev, [field]: "" }));
    dispatch({ type: ActionTypes.SET_CURRENT_PAGE, payload: 1 });
  };

  // Removed standalone clear handler (unused); use button state + direct value reset via handlePartNumberSearchChange instead.

  const handleSearchSubmit = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    dispatch({ type: ActionTypes.SET_CURRENT_PAGE, payload: 1 });
    setHasSearched(true);
    performSearch();
  };

  useEffect(() => {
    if (
      hasAutoSearchedRef.current ||
      !initialPartNumber.trim() ||
      !authState?.isAuthenticated ||
      !dealerAccount?.id
    ) {
      return;
    }
    hasAutoSearchedRef.current = true;
    handleSearchSubmit();
  }, [authState?.isAuthenticated, dealerAccount?.id]);

  const gridToolbarProps = {};

  const showPagination =
    !state.isLoading &&
    !state.error &&
    totalFilteredItems > 0 &&
    totalFilteredItems > state.pageSize;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        flexGrow: 1,
      }}
      className="other-dealer-inventory"
    >
      {/* Header rendered normally; distance + unit controls visually hidden here only.
          We do NOT modify the header component itself so header unit tests stay valid. */}
      <Box
        /* Hide only the distance/unit inputs within this page context */
        sx={{
          '& [data-testid="distance-limit-input"]': {
            display: "none !important",
          },
          '& [data-testid="distance-unit-select"]': {
            display: "none !important",
            visibility: "hidden",
          },
          "& .MuiFormControl-root label": { display: "none !important" },
        }}
      >
        <OtherDealerInventoryHeader
          distanceLimit={distanceLimit}
          setDistanceLimit={setDistanceLimit}
          distanceUnit={distanceUnit}
          setDistanceUnit={setDistanceUnit}
        />
      </Box>

      {/* Standalone Part Number search above grid (simple wrapper, reuses existing logic) */}
      <Box
        data-testid="search-part-number"
        sx={{
          display: "flex",
          gap: theme.tokens.FuelSpacing4,
          alignItems: "center",
          mb: theme.tokens.FuelSpacing4,
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            minWidth: 260,
            width: { xs: "100%", sm: "50%", md: "35%" },
            maxWidth: { xs: "100%", sm: "50%", md: "35%" },
          }}
        >
          <TextField
            id="part-number-search"
            placeholder={String(
              t(
                "d2d:otherDealerInventory.enterPartNumber",
                "Enter a Part Number"
              )
            )}
            fullWidth
            value={partNumberSearch}
            onChange={(e) => handlePartNumberSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="medium" />
                </InputAdornment>
              ),
              inputProps: { "data-testid": "search-box" },
            }}
            sx={{
              ".MuiInputBase-input": {
                fontSize: theme.tokens.FuelFontSizeSm,
                padding: "8.5px 14px",
              },
            }}
          />
        </Box>
        <Button
          variant="primary"
          color="primary"
          disabled={!partNumberSearch.trim() || state.isLoading}
          onClick={handleSearchSubmit}
          data-testid="search-button"
          sx={{
            minHeight: "40px",
          }}
        >
          {t("common:search", "Search")}
        </Button>

        {hasSearched && searchedPartNumber && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: theme.tokens.FuelSpacing2,
              }}
            >
              <Typography
                component="span"
                sx={{
                  color: theme.tokens.FuelColorTextPrimary,
                  fontWeight: theme.tokens.FuelFontWeightSemibold,
                }}
              >
                {t("d2d:otherDealerInventory.partNumber", "Part Number")}:
              </Typography>
              <Link to={`/parts/${searchedPartNumber}`}>
                {searchedPartNumber}
              </Link>
            </Box>
            {partName && (
              <Typography
                sx={{
                  color: theme.tokens.FuelColorTextPrimary,
                }}
              >
                <Box
                  component="span"
                  sx={{ fontWeight: theme.tokens.FuelFontWeightSemibold }}
                >
                  {t("d2d:otherDealerInventory.partName", "Part Name")}:
                </Box>{" "}
                {partName}
              </Typography>
            )}
          </>
        )}
      </Box>

      {hasSearched && (
        <OtherDealerInventoryGrid
          data={processedItems}
          isLoading={state.isLoading}
          sortModel={state.sortModel as GridSortModel}
          onSortModelChange={handleSortModelChange}
          filters={columnFilters}
          onFilterChange={handleColumnFilterChange}
          clearFilter={handleClearColumnFilter}
          dealerAccountId={dealerAccount?.id || ""}
          dealerServiceUrl={DEALER_SERVICE_URL}
          toolbarProps={gridToolbarProps}
        />
      )}

      {hasSearched && showPagination && (
        <StickyFooter sx={{ justifyContent: "center", padding: 2 }}>
          <Pagination
            rowsPerPage={state.pageSize}
            currentPage={state.currentPage}
            totalCount={totalFilteredItems}
            onRowsPerPageChange={(updatedRowsPerPage: number) =>
              onRowsPerPageChange(dispatch, updatedRowsPerPage)
            }
            onFirstPageClick={() => onFirstPageClick(dispatch)}
            onPreviousClick={() =>
              onPreviousPageClick(dispatch, state.currentPage)
            }
            onLastPageClick={() =>
              onLastPageClick(dispatch, state.pageSize, totalFilteredItems)
            }
            onNextClick={() =>
              onNextClick(
                dispatch,
                state.currentPage,
                totalFilteredItems,
                state.pageSize
              )
            }
          />
        </StickyFooter>
      )}
    </Box>
  );
};

export default OtherDealerInventory;
